import React, { useEffect, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import CheckBox from '../../Content/Elements/CheckBox/CheckBox';
import CommentBoxInput from './CommentBoxInput/CommentBoxInput';
import CommentUserImage from './CommentUserImage/CommentUserImage';
import { constructCn } from '../../../utilities/helperFunctions';

import './CommentBox.scss';
import {
    CommunityContext,
    PageContext,
} from '../../../contexts/ContextCreator';

const CommentBox = ({
    parentHash,
    placeholderText,
    showExpanded,
    isVip,
    authorImage,
    onSubmit,
    submitButtonText,
}) => {
    const { openCommentReply, getContent } = useContext(CommunityContext);
    const { wrapAuth, isLoggedIn } = useContext(PageContext);
    const [isActive, setActive] = useState(false);
    const [comment, setComment] = useState('');
    const [isAnonymousComment, setAnonymousComment] = useState(false);
    const [disablePost, setDisablePost] = useState(true);

    const handleCtaClick = useCallback(() => {
        if (!isLoggedIn()) return;
        openCommentReply(parentHash);
        setActive(true);
    }, [setActive, isLoggedIn, openCommentReply, parentHash]);

    const handleToggleAnonymity = useCallback(
        (checked) => setAnonymousComment(checked),
        [setAnonymousComment]
    );

    const handleOnChange = useCallback((e) => setComment(e.target.value), [
        setComment,
    ]);

    const handlePostClick = useCallback(() => {
        onSubmit(parentHash, comment, isAnonymousComment);
        setAnonymousComment(false);
        setDisablePost(true);
        setComment('');
    }, [
        onSubmit,
        parentHash,
        comment,
        isAnonymousComment,
        setAnonymousComment,
        setDisablePost,
        setComment,
    ]);

    useEffect(() => {
        if (isLoggedIn()) setActive(showExpanded);
    }, [isLoggedIn, setActive, showExpanded]);

    useEffect(() => {
        if (comment.length === 0) {
            setDisablePost(true);
            return;
        }
        setDisablePost(false);
    }, [comment.length, setDisablePost]);

    const anonCheckboxClassname = constructCn(
        'commentBox-comment-checkbox',
        'anon-comment-checkbox',
        isAnonymousComment && 'comment-anon-bold'
    );

    const commentBoxContainerClassname = constructCn(
        'commentBox-CTA-container',
        !isActive && 'center-image'
    );

    const content = getContent(parentHash);
    const authMetrics = {
        login_trigger: 'comment',
        community_content_key: `${content.type}-${content.hash}`,
        community_on_comment: content.type === 'community-comment',
    };

    return wrapAuth(
        `commentBox-${parentHash}`,
        <div className="component-CommentBox">
            <div
                id="commentBoxContainerId"
                role="button"
                tabIndex={0}
                className={commentBoxContainerClassname}
                onKeyPress={handleCtaClick}
                onClick={handleCtaClick}
            >
                <CommentUserImage isVip={isVip} authorImage={authorImage} />
                {!isActive && (
                    <div className="commentBox-commentCTA">
                        {placeholderText}
                    </div>
                )}
                {isActive && (
                    <CommentBoxInput
                        handleChange={handleOnChange}
                        placeholderText={placeholderText}
                    />
                )}
            </div>
            {isActive && (
                <div className="commentBox-button-container">
                    <div className={anonCheckboxClassname}>
                        <CheckBox
                            id="commentBox-comment-anonymous"
                            defaultChecked={isAnonymousComment}
                            handleChange={handleToggleAnonymity}
                        />
                        Post anonymously
                    </div>
                    <button
                        type="button"
                        className="comment-post-button"
                        formAction="submit"
                        disabled={disablePost}
                        onClick={handlePostClick}
                    >
                        {submitButtonText}
                    </button>
                </div>
            )}
        </div>,
        authMetrics
    );
};

CommentBox.propTypes = {
    parentHash: PropTypes.string,
    placeholderText: PropTypes.string,
    showExpanded: PropTypes.bool,
    isVip: PropTypes.bool,
    authorImage: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
    }),
    onSubmit: PropTypes.func,
    submitButtonText: PropTypes.string,
};

CommentBox.defaultProps = {
    parentHash: '',
    placeholderText: '',
    showExpanded: false,
    isVip: false,
    authorImage: {},
    onSubmit: () => {},
    submitButtonText: 'Post',
};

CommentBox.displayName = 'CommentBox';

export default CommentBox;
